import { SEO } from '@agria/theme/src/components/Seo';
import { Section } from '@agria/theme/src/components/section/Section';
import { useDataLayerPush } from '@agria/theme/src/hooks/useDataLayerPush';
import {
  TitleBlock,
  TitleBlockProps,
  StyledTitleBlock,
} from '@agria/theme/src/components/titleBlock/TitleBlock';
import React, { FC, useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { PageProps } from 'gatsby';
import { createMarkup, getQueryString, isBrowser } from '@agria/utils';
import { ExperimentsContext } from '@agria/theme/src/context/ExperimentsContext';
import { SpeciesSelection } from '@agria/theme-qab/src/components/00-species/SpeciesSelection';
import { SpeciesPromo } from '@agria/theme-qab/src/components/00-species/SpeciesPromo';
import { ActiveStepContext } from '@agria/theme-qab/src/context/ActiveStepContext';
import {
  QueryContextQueryStrings,
  QuoteContext,
} from '@agria/theme-qab/src/context/QuoteContext';
import { Hero } from '@agria/theme-qab/src/components/Hero';
import ErrorMessage from '@agria/theme/src/components/errorMessage/ErrorMessage';
import { useSiteMetadata } from '@agria/theme/src/hooks/useSiteMetadata';
import { WhenGetCover } from '@agria/theme-qab/src/components/promoBanners/WhenGetCover';
import Stack from '@agria/theme/src/components/stack/Stack';
import { ErrorBarMaintenance } from '@agria/theme/src/components/errorBarMaintenance/ErrorBarMaintenance';
import { ErrorBarNetworkIssue } from '@agria/theme/src/components/errorBarNetworkIssue/ErrorBarNetworkIssue';
import { ErrorBarWrapper } from '@agria/theme/src/components/errorBar/ErrorBar';
import { speciesUrls } from '@agria/theme-qab/src/const/speciesUrls';
import { Typography } from '@agria/theme/src/components/typography/Typography';

const StyledWhichText = styled.div`
  ${({ theme: { space } }) => css`
    margin-top: ${space.xxLarge};
    max-width: 843px;
    margin-inline: auto;
    text-align: center;

    > * {
      margin-top: ${space.medium};
  `}
`;

const TitleBlockAgria124: FC<TitleBlockProps> = ({ heading, description }) => {
  return (
    <StyledTitleBlock gap="small" centered>
      {heading && (
        <Typography component="h2" variant="headingLarge">
          {heading}
        </Typography>
      )}
      {description && (
        <Typography component="p" variant="bodyLargeBold">
          {description && typeof description === 'string'
            ? createMarkup(description)
            : description}
        </Typography>
      )}
    </StyledTitleBlock>
  );
};

const SpeciesSelectionPage = ({
  location,
}: PageProps<unknown, unknown, { message: string }>) => {
  const { activeQuote, basket, dispatch, isContinueCover } =
    useContext(QuoteContext);
  const { setActiveStep } = useContext(ActiveStepContext);
  const qs = getQueryString<QueryContextQueryStrings>(location?.search);
  const [defaultSpeciesName, setDefaultSpeciesName] = useState<
    'dog' | 'cat' | 'rabbit' | 'equine' | null
  >(null);

  const { experiments } = useContext(ExperimentsContext);

  // If there is a quoteref we need to go to the next step
  useEffect(() => {
    if (activeQuote && qs?.quoteref) {
      setActiveStep('review');
    }
  }, [activeQuote, qs?.quoteref, setActiveStep]);

  const {
    subSpecies,
    heroDescription,
    includeTrustPilot,
    heroHeading,
    includeDiscountTerms,
  } = useSiteMetadata();
  // if we have active policies in the basket and no quotes reset the basket as we are starting again
  useEffect(() => {
    if (basket && basket.Policies.length > 0 && basket.Quotes.length === 0) {
      dispatch({
        type: 'RESET',
      });
    }
  }, [basket, dispatch]);

  // if user reloads the page
  useEffect(() => {
    const handlePageReload = () => {
      dispatch({
        type: 'RESET',
      });
    };
    window.onbeforeunload = handlePageReload;

    return () => {
      window.onbeforeunload = null; // Clean up the event listener when the component unmounts.
    };
  }, [dispatch]);

  // exit continue cover
  useEffect(() => {
    if (isContinueCover) {
      dispatch({
        type: 'SET_CONTINUE_COVER',
        payload: false,
      });
    }
  }, [dispatch, isContinueCover]);

  useEffect(() => {
    if (!isBrowser) return;

    const urlParams = new URLSearchParams(window.location.search);
    const testReferrer = urlParams.get('testReferrer');

    const referrerMap = {
      'dog-insurance': 'dog',
      'cat-insurance': 'cat',
      'rabbit-insurance': 'rabbit',
      'horse-insurance': 'equine',
    } as const;

    type ReferrerKey = keyof typeof referrerMap;

    const setReferrer = (path: ReferrerKey) => {
      setDefaultSpeciesName(referrerMap[path]);
    };

    const isValidReferrer = (key: string): key is ReferrerKey =>
      Object.prototype.hasOwnProperty.call(referrerMap, key);

    if (testReferrer && isValidReferrer(testReferrer)) {
      setReferrer(testReferrer);
    } else {
      try {
        const referrerUrl = document.referrer
          ? new URL(document.referrer)
          : null;
        const path = referrerUrl?.pathname.split('/')[1];
        if (path && isValidReferrer(path)) {
          setReferrer(path);
        }
      } catch (error) {
        console.error('Error parsing referrer:', error);
        // If there's an error parsing the referrer, we don't set any default species
      }
    }

    const refSrc = urlParams.get('ref_src');
    if (!refSrc) return;
    try {
      const refSrcPathname = new URL(decodeURIComponent(refSrc)).pathname;
      const detectedSpecies = speciesUrls.get(refSrcPathname) as
        | 'dog'
        | 'cat'
        | 'rabbit'
        | 'equine';
      if (detectedSpecies) {
        setDefaultSpeciesName(detectedSpecies);
      }
    } catch (error) {
      console.error('Error parsing ref_src:', error);
      // If there's an error parsing ref_src, we don't set any default species
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const agriaEvent = urlParams.has('agria_event');
      if (agriaEvent) {
        localStorage.clear();
        sessionStorage.clear();
      }
    }
  }, []);

  return (
    <>
      <Hero
        heading={
          subSpecies
            ? `Protection for ${subSpecies}s that lasts a lifetime`
            : heroHeading
        }
        {...((subSpecies && subSpecies === 'service dog') || !includeTrustPilot
          ? {}
          : { trustpilotStars: true })}
        description={heroDescription}
        override={defaultSpeciesName || 'default'}
        jumbo
        includeDiscountTerms={includeDiscountTerms}
      />
      <ErrorBarWrapper>
        <ErrorBarMaintenance />
        <ErrorBarNetworkIssue />
      </ErrorBarWrapper>

      <Section backgroundColor="utilityBackground">
        <Stack gap="fluidLarge">
          {subSpecies && subSpecies === 'service dog' && <WhenGetCover />}
          {experiments.AGRIA124 === 'variation' && defaultSpeciesName ? (
            <TitleBlockAgria124
              heading={`Insure your ${defaultSpeciesName.replace('equine', 'horse')} in just a few steps`}
              description="More than one pet? Select another and get a multi-pet discount"
            />
          ) : (
            <TitleBlock heading="Who would you like to insure today?" />
          )}
          {location?.state?.message && (
            <ErrorMessage message={location?.state?.message} />
          )}
          <SpeciesSelection
            showDiscount
            defaultSpeciesName={defaultSpeciesName}
          />
          {(experiments.AGRIA108 === 'variation' ||
            experiments.AGRIA118 === 'variation') && (
            <StyledWhichText>
              <p>
                Agria Pet Insurance is a Which? Best&nbsp;Buy for our Lifetime,
                Lifetime Plus and Lifetime Premium dog insurance products and
                our Lifetime, Lifetime Plus, Lifetime Premium, Home and Home
                Plus cat insurance products.
              </p>
              <p>
                Which? policy score ratings are based on an analysis of policy
                cover levels conducted in November 2024.
                Best&nbsp;Buy&nbsp;providers have a high policy score and a good
                track record for paying claims.{' '}
                <a href="https://www.which.co.uk/money/insurance/pet-insurance/pet-insurer-reviews/best-and-worst-pet-insurance-adlAD3Y24cYN?utm_campaign=Bestbuy&utm_medium=endorsement&utm_source=Agria">
                  Find out more
                </a>
                .
              </p>
            </StyledWhichText>
          )}
        </Stack>
        <SpeciesPromo />
      </Section>
    </>
  );
};

export default SpeciesSelectionPage;

export const Head = () => {
  const { companyName } = useSiteMetadata();
  return (
    <SEO
      title={`Get a quote | ${companyName}`}
      meta={[
        { name: 'robots', content: 'noindex, nofollow' },
        { name: 'format-detection', content: 'telephone=no' },
      ]}
    />
  );
};
